/**
 * Componente de indicador de força da senha
 * Data: 20/07/2025
 * Desenvolvedor: Claude AI
 * 
 * Mostra força da senha em tempo real usando regras configuráveis
 * Neuroagentes - Simplex [www.simplex.tec.br]
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  Chip,
  Grid
} from '@mui/material';
import {
  Security as SecurityIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { 
  passwordValidationService, 
  PasswordRules, 
  PasswordValidationResult,
  UserContext 
} from '../../services/passwordValidationService';

interface PasswordStrengthIndicatorProps {
  password: string;
  userContext?: UserContext;
  showDetails?: boolean;
  showRules?: boolean;
  onValidation?: (result: PasswordValidationResult) => void;
  debounceMs?: number;
}

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  userContext,
  showDetails = true,
  showRules = false,
  onValidation,
  debounceMs = 500
}) => {
  const theme = useTheme();
  const [validationResult, setValidationResult] = useState<PasswordValidationResult | null>(null);
  const [rules, setRules] = useState<PasswordRules | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [lastValidatedPassword, setLastValidatedPassword] = useState<string>('');
  const [localValidation, setLocalValidation] = useState<{
    isValid: boolean;
    errors: string[];
    strengthScore: number;
  } | null>(null);

  // Carregar regras uma vez
  useEffect(() => {
    const loadRules = async () => {
      try {
        const passwordRules = await passwordValidationService.getPasswordRules();
        setRules(passwordRules);
      } catch (error) {
        console.error('Erro ao carregar regras de senha:', error);
      }
    };

    loadRules();
  }, []);

  // Validação local imediata
  useEffect(() => {
    if (password && rules) {
      const localResult = passwordValidationService.validatePasswordLocally(password, rules);
      setLocalValidation(localResult);
    } else {
      setLocalValidation(null);
    }
  }, [password, rules]);

  // Validação no servidor com debounce
  const validatePasswordDebounced = useCallback(
    async (pwd: string) => {
      if (!pwd || pwd.length === 0) {
        setValidationResult(null);
        return;
      }

      setIsValidating(true);
      
      try {
        const result = await passwordValidationService.validatePassword(pwd, userContext);
        setValidationResult(result);
        
        if (onValidation) {
          onValidation(result);
        }
      } catch (error) {
        console.error('Erro na validação:', error);
        // Em caso de erro, use apenas validação local
        setValidationResult(null);
      } finally {
        setIsValidating(false);
      }
    },
    [userContext, onValidation]
  );

  useEffect(() => {
    if (!password) {
      setValidationResult(null);
      setLastValidatedPassword('');
      return;
    }

    // Evitar validações repetidas da mesma senha
    if (password === lastValidatedPassword) {
      return;
    }

    const timer = setTimeout(() => {
      if (password !== lastValidatedPassword) {
        setLastValidatedPassword(password);
        validatePasswordDebounced(password);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [password, validatePasswordDebounced, debounceMs, lastValidatedPassword]);

  // Usar validação local enquanto server não responde
  const currentResult = validationResult || (localValidation ? {
    isValid: localValidation.isValid,
    errors: localValidation.errors,
    warnings: [],
    suggestions: [],
    strengthScore: localValidation.strengthScore,
    strengthLevel: getStrengthLevel(localValidation.strengthScore),
    strengthEmoji: passwordValidationService.getStrengthEmoji(getStrengthLevel(localValidation.strengthScore))
  } : null);

  function getStrengthLevel(score: number): string {
    if (score >= 90) return 'muito-forte';
    if (score >= 70) return 'forte';
    if (score >= 50) return 'media';
    if (score >= 30) return 'fraca';
    return 'muito-fraca';
  }

  if (!password || !currentResult) {
    return showRules && rules ? (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Requisitos da senha:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          <Chip label={`${rules.minLength}+ caracteres`} size="small" variant="outlined" />
          {rules.requireUppercase && <Chip label="Maiúscula" size="small" variant="outlined" />}
          {rules.requireLowercase && <Chip label="Minúscula" size="small" variant="outlined" />}
          {rules.requireNumbers && <Chip label="Números" size="small" variant="outlined" />}
          {rules.requireSpecialChars && <Chip label="Especiais" size="small" variant="outlined" />}
        </Box>
      </Box>
    ) : null;
  }

  const strengthColor = passwordValidationService.getStrengthColor(currentResult.strengthLevel);
  const progressValue = currentResult.strengthScore;

  return (
    <Box sx={{ mt: 1 }}>
      {/* Indicador Principal */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <SecurityIcon 
          sx={{ 
            color: strengthColor,
            fontSize: 20 
          }} 
        />
        <Box sx={{ flex: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: theme.palette.grey[200],
              '& .MuiLinearProgress-bar': {
                backgroundColor: strengthColor,
                borderRadius: 3
              }
            }}
          />
        </Box>
        <Typography 
          variant="caption" 
          sx={{ 
            color: strengthColor,
            fontWeight: 600,
            minWidth: 50
          }}
        >
          {currentResult.strengthEmoji} {currentResult.strengthLevel}
        </Typography>
      </Box>

      {/* Status de Validação */}
      {isValidating && (
        <Typography variant="caption" color="text.secondary">
          Validando...
        </Typography>
      )}

      {/* Layout Compacto - Solo validadores habilitados no banco */}
      {showDetails && rules && (
        <Box sx={{ mt: 0.5, p: 0.5, bgcolor: 'rgba(255, 255, 255, 0.02)', borderRadius: 1 }}>
          <Grid container spacing={0.5}>
            <Grid item xs={6}>
              {/* Sempre mostrar comprimento mínimo */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 0.3 }}>
                <Box 
                  sx={{ 
                    width: 14, 
                    height: 14, 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '9px',
                    fontWeight: 'bold',
                    bgcolor: password.length >= rules.minLength ? '#4caf50' : (password ? '#f44336' : '#666'),
                    color: 'white'
                  }}
                >
                  {password.length >= rules.minLength ? '✓' : '✗'}
                </Box>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.primary', lineHeight: 1.2 }}>
                  Mín. {rules.minLength} caracteres
                </Typography>
              </Box>
              
              {/* Só mostrar maiúscula se obrigatória */}
              {rules.requireUppercase && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 0.3 }}>
                  <Box 
                    sx={{ 
                      width: 14, 
                      height: 14, 
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '9px',
                      fontWeight: 'bold',
                      bgcolor: /[A-Z]/.test(password) ? '#4caf50' : (password ? '#f44336' : '#666'),
                      color: 'white'
                    }}
                  >
                    {/[A-Z]/.test(password) ? '✓' : '✗'}
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.primary', lineHeight: 1.2 }}>
                    Letra maiúscula
                  </Typography>
                </Box>
              )}
              
              {/* Só mostrar minúscula se obrigatória */}
              {rules.requireLowercase && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <Box 
                    sx={{ 
                      width: 14, 
                      height: 14, 
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '9px',
                      fontWeight: 'bold',
                      bgcolor: /[a-z]/.test(password) ? '#4caf50' : (password ? '#f44336' : '#666'),
                      color: 'white'
                    }}
                  >
                    {/[a-z]/.test(password) ? '✓' : '✗'}
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.primary', lineHeight: 1.2 }}>
                    Letra minúscula
                  </Typography>
                </Box>
              )}
            </Grid>
            
            <Grid item xs={6}>
              {/* Só mostrar números se obrigatório */}
              {rules.requireNumbers && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 0.3 }}>
                  <Box 
                    sx={{ 
                      width: 14, 
                      height: 14, 
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '9px',
                      fontWeight: 'bold',
                      bgcolor: /[0-9]/.test(password) ? '#4caf50' : (password ? '#f44336' : '#666'),
                      color: 'white'
                    }}
                  >
                    {/[0-9]/.test(password) ? '✓' : '✗'}
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.primary', lineHeight: 1.2 }}>
                    Número
                  </Typography>
                </Box>
              )}
              
              {/* Só mostrar caracteres especiais se obrigatório */}
              {rules.requireSpecialChars && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 0.3 }}>
                  <Box 
                    sx={{ 
                      width: 14, 
                      height: 14, 
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '9px',
                      fontWeight: 'bold',
                      bgcolor: new RegExp(`[${(rules.specialChars || '!@#$%^&*()_+-=[]{}|;:,.<>?').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(password) ? '#4caf50' : (password ? '#f44336' : '#666'),
                      color: 'white'
                    }}
                  >
                    {new RegExp(`[${(rules.specialChars || '!@#$%^&*()_+-=[]{}|;:,.<>?').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(password) ? '✓' : '✗'}
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.primary', lineHeight: 1.2 }}>
                    Caractere especial
                  </Typography>
                </Box>
              )}
              
              {/* Só mostrar bloqueio de dados pessoais se habilitado */}
              {rules.blockUserInfo && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <Box 
                    sx={{ 
                      width: 14, 
                      height: 14, 
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '9px',
                      fontWeight: 'bold',
                      bgcolor: (() => {
                        if (!password) return '#666';
                        
                        // Verificar se a senha contém informações do usuário
                        const lowerPassword = password.toLowerCase();
                        const hasUserInfo = [
                          userContext?.name?.toLowerCase(),
                          userContext?.email?.split('@')[0]?.toLowerCase(),
                          userContext?.firstName?.toLowerCase()
                        ].filter(Boolean).some(info => {
                          if (!info || info.length < 3) return false;
                          return lowerPassword.includes(info);
                        });
                        
                        return hasUserInfo ? '#f44336' : '#4caf50';
                      })(),
                      color: 'white'
                    }}
                  >
                    {(() => {
                      if (!password) return '✗';
                      
                      const lowerPassword = password.toLowerCase();
                      const hasUserInfo = [
                        userContext?.name?.toLowerCase(),
                        userContext?.email?.split('@')[0]?.toLowerCase(),
                        userContext?.firstName?.toLowerCase()
                      ].filter(Boolean).some(info => {
                        if (!info || info.length < 3) return false;
                        return lowerPassword.includes(info);
                      });
                      
                      return hasUserInfo ? '✗' : '✓';
                    })()}
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.primary', lineHeight: 1.2 }}>
                    Sem dados pessoais
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};